import { Moment } from "moment";
import {
  FC,
  ReactNode,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { Document, Page } from "react-pdf/dist/esm/entry.webpack";
import { Some } from "space-monad";
import styled from "styled-components";
import { ServiceDetails } from "../../store/verification/models/internal";
import { VerificationService } from "../../store/verification/service";
import { PrimaryButton } from "../button";
import { Icon } from "../icons";
import { CenteredFlex } from "../layout";
import { GlobalOverlayContext } from "../overlay/context";

import { Container, Content, IMAGE_SIZE, useKycImage, usePdf } from "./common";
import { ImageViewer } from "./viewer";

const StyledImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const Title = styled.span`
  font-size: 0.8em;
  font-weight: 500;
  color: white;
`;

const DateContainer = styled.div`
  ${CenteredFlex}
  flex-grow: 1;
`;

const FileName = styled.div`
  text-align: center;
`;

export const Image: FC<{
  file: { data: string; type: string | null };
  fileName: string;
  id: string;
  uploadedAt: Moment;
  onDownload?: (e: SyntheticEvent) => void;
}> = ({ file, fileName, id, uploadedAt, onDownload }) => {
  const { setOverlay } = useContext(GlobalOverlayContext);

  useEffect(() => {
    const fn = () => {
      setOverlay({
        title: "",
        children: null,
      });
    };

    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const onClick = useCallback(
    () =>
      setOverlay({
        title: "",
        children: <ImageViewer id={id} />,
      }),
    [setOverlay, id]
  );

  const { imageHeight, imageWidth, onLoad } = usePdf();

  const computedHeight = useMemo(() => {
    const aspectRatio = imageWidth / imageHeight;
    return aspectRatio > 1 ? IMAGE_SIZE / aspectRatio : IMAGE_SIZE;
  }, [imageHeight, imageWidth]);

  return (
    <Container onClick={onClick}>
      {file.type === "application/pdf" ? (
        <Document file={file.data} onLoadSuccess={onLoad}>
          <Page pageNumber={1} height={computedHeight} />
        </Document>
      ) : (
        <StyledImage src={file.data} alt={fileName} />
      )}

      <Content>
        <FileName>{fileName}</FileName>
        <DateContainer>
          <div>
            <Title>Upload Date</Title>
            <br />
            {uploadedAt.local().format("yyyy-MM-DD HH:mm")}
          </div>
        </DateContainer>
        {onDownload ? (
          <div>
            <PrimaryButton fullWidth onClick={onDownload}>
              <Icon type="export" />
            </PrimaryButton>
          </div>
        ) : null}
      </Content>
    </Container>
  );
};
